/**
 * 这一份产物此刻跑在哪儿。
 *
 * # 为什么这件事要单独有一个模块
 *
 * 因为它决定的不是「显示成什么样」，是「存不存在」。
 *
 * 设计稿对本机文件夹入口的要求是三个否定连着说的：
 * **不显示、不置灰、也不用浏览器目录 API 假装**。验收 §A.5 说得更死——
 * 「Web 和手机的项目选择器里没有本地文件夹入口，连禁用占位都没有」。
 *
 * 一个置灰的「选择本地开发文件夹」按钮仍然在说一句话：这个功能存在，
 * 只是你现在用不了。而在 Web 上它不是「用不了」，是**这条路根本不通**——
 * 浏览器拿不到系统级的目录权限，也没有原生 Git。给用户留一个他永远点不亮的
 * 按钮，等于让他每次看到都重新问一遍「为什么不行」。
 *
 * # 判据为什么是 Tauri 的注入对象，而不是 UA 或者构建时开关
 *
 * UA 认的是「什么浏览器」，而 macOS 客户端里跑的也是 WebKit——
 * 一个 Mac 上的 Safari 会和客户端认成同一个东西，于是 Web 上会冒出本地入口。
 * **那正是这条规则要防的事故。**
 *
 * 构建时开关（`import.meta.env`）能分开，但它分的是「这份产物是给谁编的」。
 * 而 `tauri.conf.json` 里 `frontendDist` 指的就是 `apps/console/dist`——
 * **Web 与 macOS 用的是同一份产物**，构建时根本没有两份可分。
 *
 * 剩下的唯一真判据是运行时问一句「我这个 window 里有没有 Tauri 注入的东西」。
 * 它答的正是我们要问的：有没有原生那一侧。
 */

/**
 * Tauri v2 往 window 上注入的内部对象。
 *
 * 名字是 v2 的（v1 叫 `__TAURI__`）。壳升级到 v3 时这里会认不出来，
 * 表现是**本地入口在客户端里消失**——那是安全的失败方向：
 * 少一个入口是功能缺失，多一个入口是给用户一条走不通的路。
 * 两种都得修，但前者不会骗人。
 */
const TAURI_INTERNALS = '__TAURI_INTERNALS__'

export type Surface = 'web' | 'macos'

export function detectSurface(win: unknown = globalThis): Surface {
  if (typeof win !== 'object' || win === null) return 'web'
  return TAURI_INTERNALS in win ? 'macos' : 'web'
}

/**
 * 能不能选本机开发文件夹。
 *
 * 刻意写成一个**能力**而不是「是不是 macOS」，尽管今天两者等价。
 * 调用方问的是「我能不能做这件事」，不是「用户在哪个操作系统上」——
 * 后者会诱使别处写出 `if (macos)` 然后顺手塞进一件与文件夹无关的事。
 */
export function canPickLocalFolder(surface: Surface): boolean {
  return surface === 'macos'
}
